import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { usersApi, type UserUpdateData } from '../../api/users';
import { useAuth } from '../../context/AuthContext';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, passwordError } from '../../config/password';
import type { UserRole } from '../../types/index.js';

const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System Admin',
  city_admin:   'Stadt-Admin',
  member:       'Mitglied',
  read_only:    'Lesezugriff',
};

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const id = Number(userId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.get(id),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UserUpdateData) => usersApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => usersApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const resetPwMutation = useMutation({
    mutationFn: (newPassword: string) => usersApi.resetPassword(id, newPassword),
  });

  if (isLoading) return <div className="p-6">Lädt…</div>;
  if (error || !user) return <div className="p-6 text-red-600">Nutzer nicht gefunden</div>;

  const isSelf = currentUser?.id === user.id;
  const isSystemAdmin = currentUser?.role === 'system_admin';
  const availableRoles: UserRole[] = isSystemAdmin
    ? ['system_admin', 'city_admin', 'member', 'read_only']
    : ['city_admin', 'member', 'read_only'];

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate('/settings/users')} className="text-sm text-blue-600 hover:underline">
        ← Nutzer
      </button>
      <h1 className="text-2xl font-semibold">{user.full_name}</h1>
      <p className="text-gray-500">{user.email}</p>

      <UserFields
        user={user}
        availableRoles={availableRoles}
        onSave={(p) => updateMutation.mutate(p)}
        saving={updateMutation.isPending}
      />

      <ResetPasswordCard
        onReset={(pw) => resetPwMutation.mutate(pw)}
        success={resetPwMutation.isSuccess}
        error={resetPwMutation.error?.message}
      />

      <div className="bg-white rounded shadow border border-red-200 p-5">
        <h2 className="font-semibold text-red-700 mb-2">Deaktivieren</h2>
        {isSelf ? (
          <p className="text-sm text-gray-500 italic">Eigenes Konto kann hier nicht deaktiviert werden.</p>
        ) : user.is_active ? (
          <button
            onClick={() => {
              if (confirm(`${user.email} wirklich deaktivieren?`)) deactivateMutation.mutate();
            }}
            className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Deaktivieren
          </button>
        ) : (
          <button
            onClick={() => updateMutation.mutate({ is_active: true })}
            className="px-3 py-1.5 border border-green-400 text-green-700 rounded"
          >
            Reaktivieren
          </button>
        )}
      </div>
    </div>
  );
}

interface UserFieldsProps {
  user: { full_name: string; role: UserRole; is_active: boolean };
  availableRoles: UserRole[];
  onSave: (p: UserUpdateData) => void;
  saving: boolean;
}

function UserFields({ user, availableRoles, onSave, saving }: UserFieldsProps) {
  const [fullName, setFullName] = useState(user.full_name);
  const [role, setRole] = useState<UserRole>(user.role);

  const changed = fullName !== user.full_name || role !== user.role;

  return (
    <div className="bg-white rounded shadow border border-gray-200 p-5 space-y-3">
      <h2 className="font-semibold">Stammdaten</h2>
      <label className="block">
        <span className="text-sm text-gray-700">Voller Name</span>
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded" />
      </label>
      <label className="block">
        <span className="text-sm text-gray-700">Rolle</span>
        <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} className="mt-1 w-full px-3 py-1.5 border border-gray-300 rounded">
          {availableRoles.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
      </label>
      <div className="flex justify-end">
        <button
          disabled={!changed || saving}
          onClick={() => onSave({ full_name: fullName, role })}
          className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

function ResetPasswordCard({ onReset, success, error }: { onReset: (pw: string) => void; success: boolean; error?: string }) {
  const [pw, setPw] = useState('');
  const tooShort = passwordError(pw);

  return (
    <div className="bg-white rounded shadow border border-gray-200 p-5 space-y-3">
      <h2 className="font-semibold">Passwort zurücksetzen</h2>
      <input
        type="password"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        placeholder="Neues Passwort"
        className="w-full px-3 py-1.5 border border-gray-300 rounded"
      />
      <div className="text-xs text-gray-500">{PASSWORD_HINT}</div>
      {tooShort && <div className="text-red-600 text-sm">{tooShort}</div>}
      {success && <div className="text-green-700 text-sm">Passwort zurückgesetzt.</div>}
      {error && <div className="text-red-600 text-sm">{error}</div>}
      <div className="flex justify-end">
        <button
          onClick={() => { if (pw) { onReset(pw); setPw(''); } }}
          disabled={pw.length < MIN_PASSWORD_LENGTH}
          className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Zurücksetzen
        </button>
      </div>
    </div>
  );
}
